import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, GitBranch, Newspaper, UserCheck } from 'lucide-react';
import { InlineOptionRow, ValueRow } from '../shared/property-rows';
import {
  NEWSLETTER_COMPANY, NEWSLETTER_OTHER, currentNewsletterRecipient, newsletterRecipientOptions, newsletterRecipientUpdates,
} from '../../lib/newsletter/newsletterRecipient';
import { useApp } from '../AppProvider';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { LeadPipelineMemberships } from './LeadPipelineMemberships';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { useClientPipelineMembershipStore } from '../../lib/stores/clientPipelineMembershipStore';
import { formatReferrerName } from '../../lib/utils/referrerDisplayName';

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

const chevron = (open: boolean) =>
  open ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />;

/**
 * Riga "Pipeline": valore-sunto "Vendite · Qualificato +2"; il click espande
 * la gestione completa (LeadPipelineMemberships: elenco, rimozione, aggiunta).
 */
export function LeadPipelineRow({ client }: { client: any }) {
  const [open, setOpen] = useState(false);
  const { pipelines, stages: stagesMap, fetchPipelines, fetchStagesByPipeline } = useLeadPipelineStore();
  const fetchMembershipsForClient = useClientPipelineMembershipStore((s) => s.fetchMembershipsForClient);
  const [extraCount, setExtraCount] = useState(0);

  useEffect(() => {
    fetchPipelines();
    fetchMembershipsForClient(client.id).then((m) => setExtraCount(m.length));
  }, [client.id, fetchPipelines, fetchMembershipsForClient]);

  useEffect(() => {
    if (client.pipeline_id) fetchStagesByPipeline(client.pipeline_id);
  }, [client.pipeline_id, fetchStagesByPipeline]);

  const primaryPipeline = pipelines.find((p) => p.id === client.pipeline_id);
  const primaryStage = client.pipeline_id
    ? (stagesMap.get(client.pipeline_id) || []).find((s) => s.id === client.pipeline_stage_id)
    : undefined;

  const hasAny = !!primaryPipeline || extraCount > 0;

  return (
    <div>
      <ValueRow icon={rowIcon(GitBranch)} label="Pipeline" onClick={hasAny ? () => setOpen((v) => !v) : undefined}>
        {hasAny ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {primaryPipeline && (
              <>
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: primaryPipeline.color }}
                />
                <span className="truncate">{primaryPipeline.name}</span>
                {primaryStage && (
                  <span
                    className="flex-shrink-0 rounded px-1.5 py-0.5 text-[11px]"
                    style={{ backgroundColor: `${primaryStage.color}20`, color: primaryStage.color }}
                  >
                    {primaryStage.name}
                  </span>
                )}
              </>
            )}
            {extraCount > 0 && (
              <span className="flex-shrink-0 text-[11px] text-[rgba(55,53,47,0.45)] dark:text-gray-500">
                +{extraCount}
              </span>
            )}
            {chevron(open)}
          </span>
        ) : (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">Vuoto</span>
        )}
      </ValueRow>
      {open && (
        <div className="ml-1 pb-1">
          <LeadPipelineMemberships client={client} onUpdate={() => fetchMembershipsForClient(client.id).then((m) => setExtraCount(m.length))} />
        </div>
      )}
    </div>
  );
}

/**
 * Riga "Segnalatore": nome (o "Vuoto"); il click espande email e commissione.
 */
export function LeadReferrerRow({ client }: { client: any }) {
  const [open, setOpen] = useState(false);
  const referrer = client.referrer;

  return (
    <div>
      <ValueRow icon={rowIcon(UserCheck)} label="Segnalatore" onClick={referrer ? () => setOpen((v) => !v) : undefined}>
        {referrer ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{formatReferrerName(referrer.full_name)}</span>
            {chevron(open)}
          </span>
        ) : (
          <span className="text-[rgba(55,53,47,0.45)] dark:text-gray-500">Vuoto</span>
        )}
      </ValueRow>
      {open && referrer && (
        <div className="ml-[8.5rem] space-y-0.5 pb-1">
          {referrer.email && (
            <a href={`mailto:${referrer.email}`} className="block truncate text-[12px] text-[#337EA9] hover:underline dark:text-[#529CCA]">
              {referrer.email}
            </a>
          )}
          {referrer.commission_rate !== undefined && referrer.commission_rate !== null && (
            <span className="block text-[12px] text-[#787774] dark:text-gray-400">
              Commissione: {referrer.commission_rate}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Riga "Newsletter": chi riceve la newsletter per questa anagrafica — email generica
 * (info@) oppure un singolo referente. Salvataggio immediato su newsletter_subscribed
 * + newsletter_email (vedi lib/newsletter/newsletterRecipient.ts).
 */
export function LeadNewsletterRow({
  client, canEdit, onLeadUpdate,
}: {
  client: any;
  canEdit: boolean;
  onLeadUpdate?: (id: string, updates: any) => Promise<boolean>;
}) {
  const { toast } = useApp();
  const [saving, setSaving] = useState(false);
  // Optimistic: il refetch dopo il salvataggio arriva con un attimo di ritardo
  const [pending, setPending] = useState<{ value: string | null } | null>(null);
  useEffect(() => { setPending(null); }, [client.id, client.newsletter_subscribed, client.newsletter_email]);

  // Newsletter unificata per email (opzione per org): solo iscritto / non iscritto, niente destinatario.
  // Vale anche senza email: il consenso dell'anagrafica conta per i suoi referenti.
  const unified = useSettingsStore((s) => s.settings?.newsletter_unified_by_email === true);

  const contacts = Array.isArray(client.contacts) ? client.contacts : [];
  const options = unified
    ? [{ value: NEWSLETTER_COMPANY, label: 'Iscritto', description: client.email || 'Riceve tramite i referenti', email: client.email || '' }]
    : newsletterRecipientOptions(client, contacts);
  const current = pending
    ? pending.value
    : unified
      ? (client.newsletter_subscribed ? NEWSLETTER_COMPANY : null)
      : currentNewsletterRecipient(client, contacts);
  // Indirizzo storico che non corrisponde ne' all'email principale ne' a un referente
  const rowOptions = current === NEWSLETTER_OTHER
    ? [...options, { value: NEWSLETTER_OTHER, label: 'Altro indirizzo', description: client.newsletter_email || '', email: client.newsletter_email || '' }]
    : options;

  const handleSelect = async (value: string | null) => {
    if (!onLeadUpdate || value === NEWSLETTER_OTHER) return;
    setPending({ value });
    setSaving(true);
    const ok = await onLeadUpdate(client.id, newsletterRecipientUpdates(value, options));
    setSaving(false);
    if (!ok) {
      setPending(null);
      toast.error('Errore durante il salvataggio della newsletter');
    }
  };

  return (
    <InlineOptionRow
      icon={rowIcon(Newspaper)}
      label="Newsletter"
      value={current}
      options={rowOptions.map((o) => ({ value: o.value, label: o.label, description: o.description }))}
      onSelect={handleSelect}
      canEdit={canEdit && !!onLeadUpdate && options.length > 0}
      placeholder={options.length > 0 ? 'Non iscritto' : 'Non iscritto · nessuna email'}
      clearLabel="Non iscritto"
      loading={saving}
      searchPlaceholder="Cerca destinatario..."
    />
  );
}
